"use client";

import { useState } from "react";
import { cn } from "@/lib/utils";
import Title from "@/components/title";
import { PortableText } from "@portabletext/react";
import Link from "next/link";
// Using CSS-based chevron icons for better performance

interface FAQItem {
  question: string;
  answer: string | any[]; // Can be plain text or Portable Text blocks
}

interface FAQSectionProps {
  faqs: FAQItem[];
  config: {
    enabled: boolean;
    displayStyle: "accordion" | "list";
  };
  className?: string;
}

// Helper function to render FAQ answer content
const renderFAQAnswer = (answer: string | any[]) => {
  // If answer is a string, render as plain text
  if (typeof answer === "string") {
    return (
      <p className="text-theme-light/80 leading-relaxed text-sm">{answer}</p>
    );
  }

  // If answer is an array (Portable Text blocks), render with PortableText
  if (Array.isArray(answer)) {
    return (
      <div className="text-theme-light/80 leading-relaxed prose dark:prose-invert prose-sm max-w-none">
        <PortableText
          value={answer}
          components={{
            block: {
              normal: ({ children }) => (
                <p className="text-theme-light/80 leading-relaxed mb-2">
                  {children}
                </p>
              ),
            },
            marks: {
              link: ({ children, value }) => {
                const isExternal = value?.href?.startsWith("http");
                return (
                  <Link
                    href={value?.href || "#"}
                    className="underline font-medium hover:text-theme-light transition-colors duration-200"
                    target={isExternal ? "_blank" : undefined}
                    rel={isExternal ? "noreferrer noopener" : undefined}
                  >
                    {children}
                  </Link>
                );
              },
              strong: ({ children }) => (
                <strong className="font-semibold text-theme-light">
                  {children}
                </strong>
              ),
            },
          }}
        />
      </div>
    );
  }

  // Fallback for other types
  return (
    <p className="text-theme-light/80 leading-relaxed">No content available</p>
  );
};

export default function FAQSection({
  faqs,
  config,
  className,
}: FAQSectionProps) {
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  if (!config.enabled || !faqs || faqs.length === 0) {
    return null;
  }

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  const toggleAll = () => {
    if (openItems.size === faqs.length) {
      setOpenItems(new Set());
    } else {
      setOpenItems(new Set(faqs.map((_, index) => index)));
    }
  };

  if (config.displayStyle === "list") {
    return (
      <section className={cn("mt-12 mb-8", className)}>
        <Title
          level={2}
          size="2xl"
          weight="bold"
          className="mb-6 text-accent"
        >
          Frequently Asked Questions
        </Title>

        <div className="space-y-6">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-theme-dark-background/50 border border-theme-light/10 rounded-lg p-6"
            >
              <Title
                level={3}
                size="lg"
                weight="semibold"
                className="mb-3 text-theme-light"
              >
                {faq.question}
              </Title>
              {renderFAQAnswer(faq.answer)}
            </div>
          ))}
        </div>
      </section>
    );
  }

  // Accordion style
  return (
    <section className={cn("mt-12 mb-8", className)}>
      <div className="flex justify-between items-center mb-6">
        <Title level={2} size="2xl" weight="bold" className="text-accent">
          Frequently Asked Questions
        </Title>

        <button
          onClick={toggleAll}
          className="text-sm text-muted hover:text-accent transition-colors duration-200 flex items-center gap-1"
        >
          {openItems.size === faqs.length ? "Collapse All" : "Expand All"}
          <span
            className={cn(
              "inline-block w-0 h-0 ml-1 transition-transform duration-200",
              "border-l-[4px] border-r-[4px] border-l-transparent border-r-transparent",
              openItems.size === faqs.length
                ? "border-b-[4px] border-b-current rotate-180"
                : "border-t-[4px] border-t-current"
            )}
          />
        </button>
      </div>

      <div className="space-y-2">
        {faqs.map((faq, index) => (
          <div
            key={index}
            className="bg-theme-dark-background/50 border border-theme-light/10 rounded-lg overflow-hidden"
          >
            <button
              onClick={() => toggleItem(index)}
              className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-theme-light/5 transition-colors duration-200 group"
              aria-expanded={openItems.has(index)}
              aria-controls={`faq-answer-${index}`}
            >
              <Title
                level={3}
                size="md"
                weight="medium"
                className="text-theme-light group-hover:text-accent transition-colors duration-200"
              >
                {faq.question}
              </Title>
              <span
                className={cn(
                  "inline-block w-0 h-0 ml-4 flex-shrink-0 transition-transform duration-200",
                  "border-l-[5px] border-r-[5px] border-l-transparent border-r-transparent",
                  "border-t-[5px] border-t-theme-light/60 group-hover:border-t-theme-neon",
                  "transition-colors duration-200",
                  openItems.has(index) && "rotate-180"
                )}
              />
            </button>

            {openItems.has(index) && (
              <div id={`faq-answer-${index}`} className="px-6 pb-4 pt-0">
                <div className="border-t border-theme-light/10 pt-4">
                  {renderFAQAnswer(faq.answer)}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
